// Coach detail (/coaches/:id): everything about one coach on one page, each section edited in place
// with Save/Cancel. The server decides what is visible (404 = not yours) and whether you can share.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useCrm } from '../lib/crm';
import { AdminSidebar } from '../components/AdminShell';
import CollegePicker from '../components/CollegePicker';
import ShareDialog from '../components/crm/ShareDialog';
import TextFlow from '../components/crm/TextFlow';
import { coachName, formatPhone, ORDER_KEY, STATUS_LABELS } from './CoachesPage';
import './AdminDashboard.css';
import '../components/AdminShell.css';
import './CoachesPage.css';

const KINDS = [['reply', 'Log reply'], ['call', 'Log call'], ['email', 'Log email'], ['note', 'Add note']];
const KIND_LABELS = {
  reply: 'Reply', call: 'Call', email: 'Email', note: 'Note',
  text_started: 'Text started (unconfirmed)', text_sent: 'Text sent', text_skipped: 'Text skipped',
  needs_updated: 'Needs updated', school_changed: 'School changed', reassigned: 'Reassigned',
};
const CONTACT_FIELDS = [
  ['phone', 'Phone', 'tel'], ['phone_alt', 'Other phone', 'tel'], ['email', 'Email', 'email'],
  ['email_alt', 'Other email', 'email'], ['twitter', 'Twitter', 'text'], ['recruiting_area', 'Recruiting area', 'text'],
];

// X/Twitter handle from whatever was stored: "@coach", "coach", or a twitter.com / x.com profile URL.
// X's DM compose link needs the numeric account id (recipient_id), which a handle cannot give us
// without the paid API, so the button opens the profile, where the coach's Message button is.
export function xHandle(raw) {
  const v = (raw || '').trim();
  if (!v) return null;
  const m = v.match(/^(?:https?:\/\/)?(?:www\.|mobile\.)?(?:twitter|x)\.com\/@?([A-Za-z0-9_]{1,15})(?:[/?#]|$)/i)
    || v.match(/^@?([A-Za-z0-9_]{1,15})$/);
  return m ? m[1] : null;
}

// The next coach in the list as the list was last shown (CoachesPage saves its order on row click).
export function nextCoachId(currentId) {
  let ids;
  try { ids = JSON.parse(sessionStorage.getItem(ORDER_KEY)); } catch { return null; }
  if (!Array.isArray(ids)) return null;
  const i = ids.indexOf(Number(currentId));
  return i >= 0 && i + 1 < ids.length ? ids[i + 1] : null;
}

// Server 422 codes from the coach and relationship endpoints, as text a person can act on.
const SAVE_ERRORS = {
  unknown_school: 'That school is not in our list. Clear it or pick another.',
  school_required: 'Enter the high school.',
  unitid_not_allowed: 'A high school coach cannot be linked to a college.',
  classification_high_school_only: 'Only high school coaches have a class.',
};

const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');
const stamp = (iso) => (iso ? new Date(iso).toLocaleString() : '');

// One section card: read view, or the form with Save/Cancel when editing.
function Section({ id, title, aside, editing, onEdit, onCancel, onSave, saving, error, view, form }) {
  return (
    <section className="adm__card cr-section" data-testid={id}>
      <div className="cr-section__head">
        <h2 className="cr-section__title">{title}</h2>
        {aside && <span className="cr-sub">{aside}</span>}
        {!editing && onEdit && <button type="button" className="adm-btn adm-btn--sm cr-section__edit" onClick={onEdit}>Edit</button>}
      </div>
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); onSave(); }}>
          {form}
          {error && <div role="alert" className="cr-error">{error}</div>}
          <div className="cr-modal__actions">
            <button type="button" className="adm-btn" onClick={onCancel} data-testid={`${id}_cancel`}>Cancel</button>
            <button type="submit" className="adm-btn adm-btn--primary" disabled={saving} data-testid={`${id}_save`}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      ) : view}
    </section>
  );
}

const Line = ({ label, value }) => (value ? (
  <div className="cr-line"><span className="cr-line__label">{label}</span><span>{value}</span></div>
) : null);

// onText(rel) overrides what the Text button does; by default it opens the Text flow for this one coach.
export default function CoachDetailPage({ onText }) {
  const { id } = useParams();
  const crm = useCrm();
  const location = useLocation();
  const [rel, setRel] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | missing | error
  const [activity, setActivity] = useState([]);
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState(false);
  const [texting, setTexting] = useState(false);
  const [entry, setEntry] = useState('');
  const [logging, setLogging] = useState(false);
  const [logError, setLogError] = useState('');

  const reloadActivity = useCallback(() => crm.listActivity(id).then(setActivity).catch(() => {}), [crm, id]);
  const load = useCallback(() => crm.getRelationship(id).then((r) => { setRel(r); setState('ready'); }), [crm, id]);

  useEffect(() => {
    let live = true;
    // Next coach reuses this component: drop any half-done edit so it can never save onto the new coach.
    setState('loading'); setEditing(null); setDraft({}); setError('');
    setEntry(''); setLogError(''); setSharing(false); setTexting(false);
    crm.getRelationship(id)
      .then((r) => { if (live) { setRel(r); setState('ready'); } })
      .catch((e) => { if (live) setState(e.response?.status === 404 ? 'missing' : 'error'); });
    crm.listActivity(id).then((a) => { if (live) setActivity(a); }).catch(() => {});
    return () => { live = false; };
  }, [crm, id]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const edit = (name, values) => { setEditing(name); setDraft(values); setError(''); };
  const cancel = () => { setEditing(null); setError(''); };
  const run = async (fn) => {
    setSaving(true); setError('');
    try { await fn(); setEditing(null); reloadActivity(); } catch (err) {
      const d = err.response?.data?.detail;
      setError(SAVE_ERRORS[d] || (typeof d === 'string' ? d : 'Could not save.'));
    }
    setSaving(false);
  };
  const saveFields = (keys) => run(async () => {
    const body = {};
    keys.forEach((k) => { body[k] = (draft[k] ?? '').trim() || null; });
    setRel(await crm.patchRelationship(rel.id, body));
  });
  const saveIdentity = () => run(async () => {
    const text = (v) => (v ?? '').trim() || null;
    const school = (draft.school ?? '').trim();
    const body = { first_name: text(draft.first_name), last_name: (draft.last_name ?? '').trim(), title: text(draft.title) };
    if (!body.last_name) throw new Error('Last name is required');
    if (rel.kind === 'high_school') {
      // A high school is typed text, never a unitid. A blank one is sent so the server says school_required.
      if (school !== rel.coach.school) body.school_name = school;
      body.hs_classification = text(draft.hs_class);
    } else if (draft.unitid != null) body.unitid = draft.unitid;
    else if (school && school !== rel.coach.school) body.school_name = school;
    await crm.patchCoach(rel.coach.id, body);
    await load();
  });

  const logEntry = async (kind) => {
    if (logging) return;
    setLogging(true); setLogError('');
    try {
      const a = await crm.addActivity(rel.id, kind, entry.trim());
      setActivity((list) => [a, ...list]);
      setEntry('');
    } catch {
      setLogError('Could not log that.');
    }
    setLogging(false);
  };

  const isHs = rel?.kind === 'high_school';
  const shell = (body) => (
    <div className="adm"><div className="adm__shell"><AdminSidebar active={isHs ? 'hs-coaches' : 'coaches'} /><main className="adm__main">{body}</main></div></div>
  );
  if (state === 'loading') return shell(<p className="cr-empty">Loading…</p>);
  if (state === 'missing') {
    return shell(<div className="adm__card"><p className="cr-empty" data-testid="coach_missing">Coach not found</p><p className="cr-empty"><Link to="/coaches">Back to coaches</Link></p></div>);
  }
  if (state === 'error') return shell(<div role="alert" className="settings-toast settings-toast--error">Could not load this coach.</div>);

  const c = rel.coach;
  const pub = rel.public;
  const noPhone = !rel.phone;
  const doNotContact = rel.status === 'do_not_contact';
  const noText = doNotContact || noPhone;
  const handle = xHandle(rel.twitter) || xHandle(pub?.twitter);
  const nextId = nextCoachId(id);
  const identityForm = (
    <div className="cr-form">
      <label className="adm-field"><span className="adm-field__label">First name</span>
        <input className="adm-input" value={draft.first_name ?? ''} onChange={set('first_name')} /></label>
      <label className="adm-field"><span className="adm-field__label">Last name</span>
        <input className="adm-input" value={draft.last_name ?? ''} onChange={set('last_name')} required /></label>
      <label className="adm-field"><span className="adm-field__label">Title</span>
        <input className="adm-input" value={draft.title ?? ''} onChange={set('title')} /></label>
      {isHs ? (
        <>
          <label className="adm-field"><span className="adm-field__label">High school</span>
            <input className="adm-input" value={draft.school ?? ''} onChange={set('school')} data-testid="coach_high_school" /></label>
          <label className="adm-field"><span className="adm-field__label">Class</span>
            <input className="adm-input" value={draft.hs_class ?? ''} onChange={set('hs_class')} placeholder="6A" data-testid="coach_class" /></label>
        </>
      ) : (
        <div className="adm-field"><span className="adm-field__label">School</span>
          <CollegePicker value={draft.school ?? ''} className="adm-input"
            onInputChange={(t) => setDraft((d) => ({ ...d, school: t, unitid: null }))}
            onSelect={(col) => setDraft((d) => ({ ...d, school: col.n, unitid: col.u }))}
            placeholder="Start typing, or type a school we do not list" /></div>
      )}
    </div>
  );

  return shell(
    <>
      {notice && <div className="settings-toast" role="status" data-testid="coach_notice">{notice}</div>}
      <p className="cr-back">
        {isHs
          ? <Link to="/coaches/high-school" data-testid="coach_back">&larr; High School Contacts</Link>
          : <Link to="/coaches" data-testid="coach_back">&larr; College Contacts</Link>}
        {nextId && <Link to={`/coaches/${nextId}`} className="adm-btn adm-btn--sm" data-testid="coach_next">Next coach &rarr;</Link>}
      </p>
      <div className="cr-detail">
        <div className="cr-detail__main">
          <Section id="coach_identity" title={coachName(c)} editing={editing === 'identity'} saving={saving} error={error}
            onEdit={() => edit('identity', { first_name: c.first_name || '', last_name: c.last_name || '', title: c.title || '', school: c.school || '', unitid: null, hs_class: rel.classification || '' })}
            onCancel={cancel} onSave={saveIdentity} form={identityForm}
            view={(
              <>
                <Line label="Title" value={c.title} />
                {isHs ? (
                  <>
                    <Line label="High school" value={c.school} />
                    <Line label="Class" value={rel.classification} />
                  </>
                ) : <Line label="School" value={[c.school, c.division].filter(Boolean).join(' · ')} />}
                {rel.school_history?.length > 0 && (
                  <Line label="Previously" value={rel.school_history.map((h) => h.school).join(', ')} />
                )}
              </>
            )} />

          {pub && (
            <section className="adm__card cr-section cr-public" data-testid="coach_public">
              <div className="cr-section__head">
                <h2 className="cr-section__title">From the school website</h2>
                <span className="adm-chip">{pub.source}</span>
              </div>
              <Line label="Email" value={pub.email} />
              <Line label="Phone" value={pub.phone} />
              <Line label="Twitter" value={pub.twitter} />
            </section>
          )}

          <Section id="coach_contact" title="Our contact info" editing={editing === 'contact'} saving={saving} error={error}
            onEdit={() => edit('contact', { ...Object.fromEntries(CONTACT_FIELDS.map(([k]) => [k, rel[k] || ''])), status: rel.status })}
            onCancel={cancel}
            onSave={() => run(async () => {
              const body = {};
              CONTACT_FIELDS.forEach(([k]) => { body[k] = (draft[k] ?? '').trim() || null; });
              body.status = draft.status;
              setRel(await crm.patchRelationship(rel.id, body));
            })}
            form={(
              <div className="cr-form">
                {CONTACT_FIELDS.map(([k, label, type]) => (
                  <label key={k} className="adm-field"><span className="adm-field__label">{label}</span>
                    <input className="adm-input" type={type} value={draft[k] ?? ''} onChange={set(k)} /></label>
                ))}
                <label className="adm-field"><span className="adm-field__label">Status</span>
                  <select className="adm-select" value={draft.status} onChange={set('status')}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></label>
              </div>
            )}
            view={(
              <>
                <Line label="Phone" value={formatPhone(rel.phone)} />
                <Line label="Other phone" value={formatPhone(rel.phone_alt)} />
                <Line label="Email" value={rel.email} />
                <Line label="Other email" value={rel.email_alt} />
                <Line label="Twitter" value={rel.twitter} />
                <Line label="Recruiting area" value={rel.recruiting_area} />
                <Line label="Status" value={STATUS_LABELS[rel.status] || rel.status} />
              </>
            )} />

          <Section id="coach_needs" title="Needs" aside={rel.needs_updated_at ? `updated ${day(rel.needs_updated_at)}` : null}
            editing={editing === 'needs'} saving={saving} error={error}
            onEdit={() => edit('needs', { needs: rel.needs || '', transfer_needs: rel.transfer_needs || '' })}
            onCancel={cancel} onSave={() => saveFields(['needs', 'transfer_needs'])}
            form={(
              <div className="cr-form">
                <label className="adm-field cr-form__wide"><span className="adm-field__label">Needs</span>
                  <textarea className="adm-input cr-textarea" value={draft.needs ?? ''} onChange={set('needs')} /></label>
                <label className="adm-field cr-form__wide"><span className="adm-field__label">Transfer needs</span>
                  <textarea className="adm-input cr-textarea" value={draft.transfer_needs ?? ''} onChange={set('transfer_needs')} /></label>
              </div>
            )}
            view={(
              <>
                <Line label="Needs" value={rel.needs} />
                <Line label="Transfer needs" value={rel.transfer_needs} />
                {!rel.needs && !rel.transfer_needs && <p className="cr-muted">Nothing recorded yet.</p>}
              </>
            )} />

          <Section id="coach_notes" title="Notes" editing={editing === 'notes'} saving={saving} error={error}
            onEdit={() => edit('notes', { notes: rel.notes || '' })}
            onCancel={cancel} onSave={() => saveFields(['notes'])}
            form={<textarea className="adm-input cr-textarea" aria-label="Notes" value={draft.notes ?? ''} onChange={set('notes')} />}
            view={rel.notes ? <p className="cr-notes">{rel.notes}</p> : <p className="cr-muted">No notes yet.</p>} />
        </div>

        <aside className="cr-detail__side">
          <div className="adm__card cr-section">
            <div className="cr-actions">
              <button type="button" className="adm-btn adm-btn--dark" disabled={noText}
                title={doNotContact ? 'Do not contact' : (noPhone ? 'No usable phone' : undefined)}
                onClick={() => (onText ? onText(rel) : setTexting(true))} data-testid="coach_text">Text</button>
              {handle && !doNotContact && (
                <a className="adm-btn" href={`https://x.com/${handle}`} target="_blank" rel="noopener noreferrer"
                  title={`Open @${handle} on X to send a message`} data-testid="coach_message_x">Message on X</a>
              )}
              {rel.can_share && (
                <button type="button" className="adm-btn" onClick={() => setSharing(true)} data-testid="coach_share">Share</button>
              )}
            </div>
          </div>

          <section className="adm__card cr-section" data-testid="coach_activity">
            <h2 className="cr-section__title">Activity</h2>
            <textarea className="adm-input cr-textarea" aria-label="Activity note" placeholder="What happened?" value={entry}
              onChange={(e) => setEntry(e.target.value)} data-testid="activity_body" />
            <div className="cr-actions">
              {KINDS.map(([k, label]) => (
                <button key={k} type="button" className="adm-btn adm-btn--sm" disabled={logging} onClick={() => logEntry(k)}
                  data-testid={`activity_log_${k}`}>{label}</button>
              ))}
            </div>
            {logError && <div role="alert" className="cr-error">{logError}</div>}
            <ul className="cr-list">
              {activity.map((a) => (
                <li key={a.id} className="cr-activity" data-testid="activity_item">
                  <div><strong>{KIND_LABELS[a.kind] || a.kind}</strong> <span className="cr-sub">{a.actor?.name} · {stamp(a.created_at)}</span></div>
                  {a.body && <div>{a.body}</div>}
                </li>
              ))}
            </ul>
            {activity.length === 0 && <p className="cr-muted">No activity yet.</p>}
          </section>
        </aside>
      </div>
      {texting && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-panel cr-modal">
            <button type="button" className="modal-close" onClick={() => { setTexting(false); reloadActivity(); }} aria-label="Close">&times;</button>
            <TextFlow ids={[rel.id]} rows={[rel]} onDone={() => { setTexting(false); reloadActivity(); }} />
          </div>
        </div>
      )}
      {sharing && <ShareDialog relationshipId={rel.id} onClose={() => setSharing(false)} />}
    </>,
  );
}
